import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Numeric } from '@blade/ui-kit'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { Trend } from '@root/entities/trend'
import { TrendIcon } from '@root/entities/trend/ui'

import { PairActivitySnackbarBase } from './pair-activity-snackbar-base'

import type { PairActivity } from '../model'

type Props = {
  activity: PairActivity.Growth
  onClose?: () => void
}

export const PairActivitySnackbarGrowth = ({ activity, onClose }: Props) => {
  const { t } = useTranslation()

  const trend = Trend.of(activity.growthPercents)
  const { currency } = activity

  return (
    <PairActivitySnackbarBase onClose={onClose} activity={activity}>
      <Root>
        <CurrencyDetails>
          <CurrencyIcon currency={currency} size={24} withNetwork />
          <Ticker>{currency.title}</Ticker>
        </CurrencyDetails>
        <Trade data-trend={trend}>
          <Column>
            <TrendIcon trend={trend} variant="candle" size={11} />
            <PnlCell>
              <Numeric
                value={activity.growthPercents}
                preset="percents"
                forceSign
              />
              %
            </PnlCell>
          </Column>
          <Separator />
          <Column>
            {t('trade.market-cap-short')}{' '}
            <Numeric
              value={activity.marketCapUsd}
              preset="currency-amount-short"
              currency="$"
            />
          </Column>
          <Separator />
          <Column>
            {t('common.price')}{' '}
            <Numeric
              value={activity.priceUsd}
              preset="currency-amount-short"
              currency="$"
            />
          </Column>
        </Trade>
      </Root>
    </PairActivitySnackbarBase>
  )
}

const Root = styled.div`
  display: grid;
  align-items: center;
  grid-auto-flow: column;
  gap: 5px;
`

const CurrencyDetails = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 8px;

  justify-items: start;
  justify-content: start;
`

const Ticker = styled.span`
  color: var(--text-12);

  font-size: 12px;
  font-weight: 600;
  line-height: 16px;

  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;

  width: 100%;
`

const Trade = styled.div`
  color: var(--text-11);

  display: grid;
  grid-template-columns: auto 1px auto 1px auto;
  align-items: center;
  gap: 8px;

  &[data-trend='up'] {
    color: var(--up-9);
  }
  &[data-trend='down'] {
    color: var(--down-9);
  }
`

const Separator = styled.div`
  background: var(--backgroundA-4);
  width: 1px;
  height: 14px;
`

const Column = styled.span`
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 2px;
`

const PnlCell = styled.span``
